import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { cn } from '@heidi/utils';
import { lazy, Suspense, useState } from 'react';
import { useIntl } from 'react-intl';

import { Heidi2BookDemoModalLoading } from './Heidi2BookDemoModalLoading';
import {
  heidi2BookMeetingHeading,
  type Heidi2BookMeetingStep,
} from './heidi2BookMeeting.heading';
import { heidi2OnboardingMessages } from './heidi2Onboarding.messages';
import type { Heidi2BookDemoPort } from './heidi2Onboarding.types';

// `React.lazy` rather than `next/dynamic`: this zone may not reach for Next
// beyond `next/image`, and the dialog only renders client-side anyway.
const Heidi2BookDemoModalContent = lazy(() =>
  import('./Heidi2BookDemoModalContent').then((module) => ({
    default: module.Heidi2BookDemoModalContent,
  }))
);

const Heidi2BookMeetingContent = lazy(() =>
  import('./Heidi2BookMeetingContent').then((module) => ({
    default: module.Heidi2BookMeetingContent,
  }))
);

interface Heidi2BookDemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Supplies the prefill and performs the submission. */
  bookDemo: Heidi2BookDemoPort;
  /**
   * A HubSpot scheduler for the contact's country, when one exists. Present
   * means the user picks a slot in-product; absent falls back to the form,
   * which files a lead for a human to follow up.
   */
  meetingUrl?: string;
  /**
   * A booking was taken. Fires once HubSpot accepts, before the user dismisses
   * the confirmation, so the host can record it even if the tab goes away.
   */
  onBooked?: () => void;
}

/**
 * Books time with a Heidi expert, either way the contact's country supports:
 * the two-step scheduler that reads and books the country's HubSpot link, or
 * the details form whose submission the host files as a lead. Opened from the
 * tour's finale.
 *
 * The title and subtitle stay in the shell so the dialog has its accessible
 * name from the first frame; the scheduler and the form only mount once the
 * open animation has finished. The scheduler retitles the dialog per step, so
 * the step it is on lives here rather than inside the lazy chunk.
 */
export const Heidi2BookDemoModal = ({
  open,
  onOpenChange,
  bookDemo,
  meetingUrl,
  onBooked,
}: Heidi2BookDemoModalProps) => {
  const intl = useIntl();
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [meetingStep, setMeetingStep] = useState<Heidi2BookMeetingStep>('time');

  const meeting = bookDemo.meeting;
  const isScheduling = !!meetingUrl && !!meeting;
  const heading = isScheduling
    ? heidi2BookMeetingHeading(meetingStep)
    : {
        title: heidi2OnboardingMessages.bookDemoTitle,
        subtitle: heidi2OnboardingMessages.bookDemoSubtitle,
        hidden: false,
      };

  const close = () => {
    onOpenChange(false);
    setMeetingStep('time');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setMeetingStep('time');
      }}
      onOpenChangeComplete={setIsDialogVisible}
    >
      <DialogContent
        className={cn(
          // A full-screen sheet on mobile: the design system already drops the
          // corner radius below `sm`, and a phone has no room to spare. The
          // `p-6` on the content is what keeps the copy off the edges.
          'h-dvh max-h-dvh max-w-none rounded-none',
          // Scroll rather than spill. Radix centres the dialog, so overflow
          // hangs off the top and the bottom at once and neither the header
          // nor the footer is reachable.
          'overflow-y-auto',
          // From `sm` up it is a centred dialog again, sized to its content —
          // two columns for the scheduler, one for the lead form. Unqualified,
          // that width was wider than a phone and clipped the right column.
          'sm:h-auto sm:max-h-[calc(100dvh-4rem)] sm:rounded-2xl',
          isScheduling ? 'sm:max-w-fit' : 'sm:max-w-lg'
        )}
        data-testid="heidi2-onboarding-book-demo-modal"
      >
        {/* `sr-only` on the confirmation step, which centres its own heading in
            the body — the header stays so the dialog keeps its name. */}
        <DialogHeader className={heading.hidden ? 'sr-only' : undefined}>
          {/* Exposure rather than the body font the h4 variant carries: this is
              the dialog's display headline as well as its accessible name. */}
          <DialogTitle className="font-exposure font-normal text-text-primary">
            {intl.formatMessage(heading.title)}
          </DialogTitle>
          <DialogDescription>{intl.formatMessage(heading.subtitle)}</DialogDescription>
        </DialogHeader>

        {!isDialogVisible ? (
          <Heidi2BookDemoModalLoading />
        ) : isScheduling ? (
          <Suspense fallback={<Heidi2BookDemoModalLoading />}>
            <Heidi2BookMeetingContent
              meeting={meeting}
              meetingUrl={meetingUrl}
              contact={bookDemo.contact}
              PhoneInput={bookDemo.PhoneInput}
              step={meetingStep}
              onStepChange={setMeetingStep}
              onCancel={close}
              onBooked={() => onBooked?.()}
              onDone={close}
            />
          </Suspense>
        ) : (
          <Suspense fallback={<Heidi2BookDemoModalLoading />}>
            <Heidi2BookDemoModalContent
              bookDemo={bookDemo}
              onCancel={close}
              onSent={close}
            />
          </Suspense>
        )}
      </DialogContent>
    </Dialog>
  );
};
